// The photo block inside a wish's block list.
//
// Extracted from `WishBlocks.tsx` to pay the route-size ratchet
// (AGENTS.md §3 #10). W39.54 added the "add from an album" entry point, which
// took that file from 19.3 KB to 20.9 KB; the ceiling says 超就当场拆, and this
// block is the part with a clean seam — it owns one query, one mutation and one
// sheet, and nothing else in the file touches any of them.
//
// It is also where the one API-with-no-door lived. `POST
// /wishes/{wish_id}/blocks/{block_id}/photos` had existed since W3 and only
// `DELETE` had a caller, so the block could show and remove photos but had no
// way to add any.

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { WishPhotoPickerSheet } from "@/components/wish/WishPhotoPickerSheet";
import { useToast } from "@/components/ui/toast";
import {
  detachPhoto,
  listPhotoItems,
  type PhotoItemOut,
} from "@/lib/api/wishBlocks";
import { type WishBlockOut } from "@/lib/api/wishlist";

export function PhotoBlock({ block }: { block: WishBlockOut }) {
  const wishId = block.wish_id;
  const queryClient = useQueryClient();
  const toast = useToast();
  // W39.54 — the block could show and remove photos but had no way to *add*
  // any: `POST /wishes/{id}/blocks/{id}/photos` had existed since W3 with no UI
  // able to reach it, so a user who had never uploaded straight into the block
  // saw a permanent 「（尚未加入照片）」.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerError, setPickerError] = useState<string | null>(null);
  const list = useQuery({
    queryKey: ["wishes", "detail", wishId, "blocks", block.id, "photos"],
    queryFn: () => listPhotoItems(wishId, block.id),
    staleTime: 30_000,
  });
  const detachMut = useMutation({
    mutationFn: async (albumItemId: string) =>
      detachPhoto(wishId, block.id, albumItemId),
    onSuccess: () => {
      // W50.3 D-2 — success only; failure is the app-wide reporter's job.
      toast.push({ message: "已移除照片", variant: "you" });
      void queryClient.invalidateQueries({
        queryKey: ["wishes", "detail", wishId, "blocks", block.id, "photos"],
      });
      void queryClient.invalidateQueries({ queryKey: ["wishes", "detail"] });
    },
  });
  const items: PhotoItemOut[] = list.data ?? [];
  return (
    <div
      data-testid={`photo-block-${block.id}`}
      className="flex flex-col gap-s-2"
    >
      {list.isLoading ? (
        <p className="text-meta text-ink-3">載入中…</p>
      ) : items.length === 0 ? (
        <>
          <p className="text-meta text-ink-3">（尚未加入照片）</p>
          {/* Only while empty — below a populated grid it reads as if the
              photos are not the right kind. See ADR-0121 and W39.38. */}
          <p className="text-meta text-ink-3">這個區塊用來放你們一起挑的照片。</p>
        </>
      ) : (
        <ul
          data-testid={`photo-grid-${block.id}`}
          className="grid grid-cols-3 gap-1"
        >
          {items.map((it) => (
            <li
              key={it.album_item_id}
              data-testid={`photo-item-${it.album_item_id}`}
              className="relative aspect-square rounded-md overflow-hidden bg-paper-deep"
            >
              {it.url ? (
                <img
                  src={it.url}
                  alt={it.caption ?? ""}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="absolute inset-0 flex items-center justify-center text-meta text-ink-3">
                  無圖
                </span>
              )}
              <button
                type="button"
                aria-label="移除"
                onClick={() => detachMut.mutate(it.album_item_id)}
                disabled={detachMut.isPending}
                data-testid={`photo-detach-${it.album_item_id}`}
                className="press absolute top-1 right-1 min-h-7 min-w-7 px-1 rounded-full bg-paper/90 text-meta text-ink-1 hover:bg-paper disabled:opacity-50"
              >
                移除
              </button>
            </li>
          ))}
        </ul>
      )}

      {pickerError ? (
        <p className="text-body-sm text-partner-strong" role="alert" data-testid={`photo-block-error-${block.id}`}>
          {pickerError}
        </p>
      ) : null}

      <button
        type="button"
        onClick={() => {
          setPickerError(null);
          setPickerOpen(true);
        }}
        data-testid={`photo-add-${block.id}`}
        className="press min-h-[44px] w-fit rounded-full border border-hairline px-s-4 text-body-sm text-ink-1 hover:bg-black/5 transition-colors"
      >
        {items.length === 0 ? "從相簿挑照片" : "再加幾張"}
      </button>

      <WishPhotoPickerSheet
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        wishId={wishId}
        blockId={block.id}
        busy={detachMut.isPending}
        onAttached={() => {
          setPickerError(null);
          void queryClient.invalidateQueries({
            queryKey: ["wishes", "detail", wishId, "blocks", block.id, "photos"],
          });
          void queryClient.invalidateQueries({ queryKey: ["wishes", "detail"] });
        }}
        onError={setPickerError}
      />
    </div>
  );
}
